import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Badge,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Fab
} from '@mui/material';
import {
  Dashboard,
  EmojiEvents,
  WorkspacePremium,
  Title,
  People,
  TrendingUp,
  Assessment,
  Settings,
  Article,
  Flag,
  SupportAgent,
  BugReport,
  Api,
  Chat,
  Visibility,
  Hub,
  AdminPanelSettings,
  Movie,
  Tv,
  Analytics,
  Security,
  Notifications,
  Schedule,
  Backup,
  RestoreFromTrash,
  Upload,
  Download,
  Add,
  Launch,
  QuickAccessWallet,
  Speed,
  ManageAccounts,
  ContentModeration,
  NewReleases,
  DataUsage,
  MonitorHeart,
  PlaylistPlay
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin } from '../../utils/adminUtils';

const AdminControlCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [speedDialOpen, setSpeedDialOpen] = useState(false);

  if (!isAdmin(user)) {
    return (
      <Alert severity="error">
        Access denied. Admin privileges required.
      </Alert>
    );
  }

  const adminSections = [
    {
      title: 'Core Management',
      color: 'primary',
      items: [
        {
          name: 'Admin Dashboard',
          description: 'Main admin control panel with overview stats',
          icon: <Dashboard />,
          path: '/admin',
          status: 'active',
          priority: 'high'
        },
        {
          name: 'Achievement System',
          description: 'Manage achievements, badges, and titles',
          icon: <EmojiEvents />,
          path: '/admin/achievements',
          status: 'active',
          priority: 'high'
        },
        {
          name: 'News Management',
          description: 'Create and manage news articles',
          icon: <Article />,
          path: '/admin/news',
          status: 'active',
          priority: 'high'
        },
        {
          name: 'Content Management',
          description: 'Manage movies and shows with MongoDB API',
          icon: <Movie />,
          path: '/admin/content',
          status: 'active',
          priority: 'high'
        },
        {
          name: 'HypeRating Manager',
          description: 'Manually boost hype ratings for movies and shows',
          icon: <TrendingUp />,
          path: '/admin/hyperating',
          status: 'active',
          priority: 'high'
        }
      ]
    },
    {
      title: 'Content Moderation',
      color: 'warning',
      items: [
        {
          name: 'User Reports',
          description: 'Review and moderate user reports',
          icon: <Flag />,
          path: '/admin/reports',
          status: 'active',
          priority: 'high'
        },
        {
          name: 'Content Reports',
          description: 'Moderate reported movies and shows',
          icon: <Movie />,
          path: '/admin/content-reports',
          status: 'active',
          priority: 'medium'
        },
        {
          name: 'Review Moderation',
          description: 'Moderate user reviews and ratings',
          icon: <Assessment />,
          path: '/admin/review-moderation',
          status: 'active',
          priority: 'medium'
        }
      ]
    },
    {
      title: 'Support & Tickets',
      color: 'info',
      items: [
        {
          name: 'Support Dashboard',
          description: 'Overview of support tickets and issues',
          icon: <SupportAgent />,
          path: '/admin/support',
          status: 'active',
          priority: 'high'
        },
        {
          name: 'Ticket Manager',
          description: 'Manage and respond to support tickets',
          icon: <ManageAccounts />,
          path: '/admin/support/tickets',
          status: 'active',
          priority: 'high'
        }
      ]
    },
    {
      title: 'Testing & Development',
      color: 'secondary',
      items: [
        {
          name: 'Achievement Testing',
          description: 'Test achievement system functionality',
          icon: <BugReport />,
          path: '/admin/achievement-test',
          status: 'active',
          priority: 'low'
        },
        {
          name: 'Leaderboard Data',
          description: 'Test and verify leaderboard data',
          icon: <TrendingUp />,
          path: '/admin/leaderboard-data',
          status: 'active',
          priority: 'low'
        },
        {
          name: 'Event API Demo',
          description: 'Test real-time notifications and events',
          icon: <Api />,
          path: '/event-api-demo',
          status: 'active',
          priority: 'low'
        },
        {
          name: 'Presence Demo',
          description: 'Test user presence tracking',
          icon: <Visibility />,
          path: '/presence-demo',
          status: 'active',
          priority: 'low'
        },
        {
          name: 'Integration Test',
          description: 'Comprehensive Event API testing',
          icon: <Hub />,
          path: '/integration-test',
          status: 'active',
          priority: 'low'
        }
      ]
    },
    {
      title: 'Data Management',
      color: 'success',
      items: [
        {
          name: 'Watchlist Test',
          description: 'Test watchlist functionality',
          icon: <Visibility />,
          path: '/test/watchlist',
          status: 'active',
          priority: 'low'
        },
        {
          name: 'Watched Cleanup',
          description: 'Clean up watched movie data',
          icon: <RestoreFromTrash />,
          path: '/test/cleanup-watched',
          status: 'active',
          priority: 'low'
        },
        {
          name: 'Watched Toggle Test',
          description: 'Test watched status toggle',
          icon: <Visibility />,
          path: '/test/watched-toggle',
          status: 'active',
          priority: 'low'
        },
        {
          name: 'Legacy Sync',
          description: 'Sync legacy table data',
          icon: <Backup />,
          path: '/test/legacy-sync',
          status: 'active',
          priority: 'low'
        }
      ]
    }
  ];
  



  const quickActions = [
    {
      icon: <EmojiEvents />,
      name: 'Create Achievement',
      action: () => navigate('/admin')
    },
    {
      icon: <Article />,
      name: 'Create News',
      action: () => navigate('/admin/news')
    },
    {
      icon: <Flag />,
      name: 'View Reports',
      action: () => navigate('/admin/reports')
    },
    {
      icon: <SupportAgent />,
      name: 'Support Tickets',
      action: () => navigate('/admin/support/tickets')
    },
    {
      icon: <TrendingUp />,
      name: 'HypeRating',
      action: () => navigate('/admin/hyperating')
    },
    {
      icon: <Analytics />,
      name: 'Analytics',
      action: () => console.log('Analytics coming soon')
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'beta': return 'warning';
      case 'disabled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const AdminSection = ({ section }) => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {section.title}
          </Typography>
          <Chip 
            label={`${section.items.length} tools`} 
            size="small" 
            color={section.color}
            variant="outlined"
          />
        </Box>
        
        <Grid container spacing={2}>
          {section.items.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper 
                sx={{ 
                  p: 2, 
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  }
                }}
                onClick={() => navigate(item.path)}
              >
                <Box display="flex" alignItems="flex-start" mb={1}>
                  <Avatar 
                    sx={{ 
                      bgcolor: `${section.color}.main`, 
                      width: 40, 
                      height: 40,
                      mr: 2
                    }}
                  >
                    {item.icon}
                  </Avatar>
                  <Box flexGrow={1}>
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {item.name}
                      </Typography>
                      <Box ml="auto" display="flex" gap={0.5}>
                        <Chip 
                          label={item.status} 
                          size="small" 
                          color={getStatusColor(item.status)}
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        <Chip 
                          label={item.priority} 
                          size="small" 
                          color={getPriorityColor(item.priority)}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button 
                    size="small" 
                    endIcon={<Launch />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(item.path);
                    }}
                  >
                    Open
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  const SystemOverview = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          System Overview
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <MonitorHeart color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="success.main">
                Healthy
              </Typography>
              <Typography variant="body2" color="text.secondary">
                System Status
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <DataUsage color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="primary.main">
                {adminSections.reduce((total, section) => total + section.items.length, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Admin Tools
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Speed color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="warning.main">
                Fast
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Response Time
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Security color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="info.main">
                Secure
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Access Control
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const QuickAccessPanel = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Quick Access
        </Typography>
        
        <Grid container spacing={2}>
          {quickActions.map((action, index) => (
            <Grid item xs={6} sm={4} md={2.4} key={index}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={action.icon}
                onClick={action.action}
                sx={{ 
                  py: 1.5,
                  flexDirection: 'column',
                  gap: 0.5,
                  '& .MuiButton-startIcon': {
                    margin: 0
                  }
                }}
              >
                <Box sx={{ fontSize: '0.8rem' }}>
                  {action.name}
                </Box>
              </Button>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Admin Control Center
        </Typography>
        <Box display="flex" gap={2}>
          <Tooltip title="System is running smoothly">
            <Chip 
              icon={<MonitorHeart />} 
              label="System Healthy" 
              color="success" 
              variant="outlined"
            />
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => navigate('/admin')}
          >
            Main Dashboard
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Welcome to the Admin Control Center. All admin tools and dashboards are organized below for easy access.
        Use the quick access panel for frequently used functions.
      </Alert>

      <SystemOverview />
      <QuickAccessPanel />

      {adminSections.map((section, index) => (
        <AdminSection key={index} section={section} />
      ))}

      {/* Speed Dial for Quick Actions */}
      <SpeedDial
        ariaLabel="Quick Admin Actions"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        icon={<SpeedDialIcon />}
        open={speedDialOpen}
        onClose={() => setSpeedDialOpen(false)}
        onOpen={() => setSpeedDialOpen(true)}
      >
        {quickActions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            onClick={() => {
              action.action();
              setSpeedDialOpen(false);
            }}
          />
        ))}
      </SpeedDial>
    </Box>
  );
};

export default AdminControlCenter;